import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { PhoneTypes } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { selectPhones } from '../../phones/extension-phone-register/state/extension-phone-register.reducer';
import { PhoneRegisterListItem } from '../../phones/models/phone-register-results.model';
import { AddPhoneData } from '../models/add-phone-data.model';
import { DeviceDetailView } from '../models/device-detail-view.model';
import { PhoneDetailView } from '../models/phone-detail-view.model';
import { SipUserMetaData } from '../models/sip-user-metadata.model';
import { PhoneDetailFormService } from '../services/phone-detail-form.service';
import { addPhoneToRegister } from '../state/add-phone.actions';
import { getAddPhoneCallState } from '../state/add-phone.selectors';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PhoneDetailFormService],
    selector: 'ndt-add-phone',
    styleUrls: ['./add-phone.component.scss'],
    templateUrl: './add-phone.component.html',
    standalone: false
})
export class AddPhoneComponent implements FormComponent {
   accountName: string;

   form: UntypedFormGroup;

   phone: PhoneDetailView;

   device: DeviceDetailView;

   deviceId: number;

   accountId: number;

   phoneTypes: typeof PhoneTypes = PhoneTypes;

   callState$: Observable<CallState>;

   isSystemUser$: Observable<boolean>;

   constructor(
      protected store: Store,
      protected formService: PhoneDetailFormService,
      @Inject(MAT_DIALOG_DATA) public data: AddPhoneData,
      private dialogService: LcsDialogService
   ) {
      this.accountId = data.accountId;
      this.accountName = data.accountName;
      this.phone = {
         devices: data.devices ?? [],
         baseStationHandsets: data.baseStationHandsets ?? [],
         extensionId: data.extensionId,
         phoneId: data.phoneId ?? 0,
         handsetIpei: data.handsetIpei,
         phoneTypeId: data.phoneType,
         numberOfLineKeys: data.numberOfLineKeys,
         sipUsers: data.sipUsers ?? [],
         sipUserMetaData: this.sipUsersMetaDataConversion(data.sipUsers),
      };
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
      this.callState$ = this.store.pipe(select(getAddPhoneCallState));
      this.form = this.formService.form;
      if (this.phone.phoneTypeId === PhoneTypes.Softphone) {
         this.formService.addSipUser();
      }
      if (this.phone.phoneTypeId === PhoneTypes.Hardphone) {
         this.formService.addHardphoneToExtension();
      }
      if (this.phone.phoneTypeId === PhoneTypes.Handset) {
         this.formService.addHandset();
      }
      this.formService.updatePhone(this.phone);
   }

   sipUsersMetaDataConversion(sipUsers: any): SipUserMetaData[] {
      return sipUsers
         ?.map((user) => {
            const internalIps = user?.internalIps?.split(',').map((ip) => ip.trim()) || [];
            const externalIps = user?.externalIps?.split(',').map((ip) => ip.trim()) || [];
            const userAgents = user?.userAgents?.split(',').map((ua) => ua.trim()) || [];

            const maxLength = Math.max(internalIps.length, externalIps.length, userAgents.length);
            const rows: SipUserMetaData[] = [];

            for (let i = 0; i < maxLength; i++) {
               if (internalIps[i] || externalIps[i] || userAgents[i])
                  rows.push({
                     internalIp: internalIps[i] ?? '',
                     externalIp: externalIps[i] ?? '',
                     userAgent: userAgents[i] ?? '',
                  });
            }

            return rows;
         })
         .reduce((acc, val) => acc.concat(val), []);
   }

   onCloseClick(): void {
      this.dialogService.close();
   }

   onCreate(): void {
      if (this.form.dirty) {
         if (this.phone.phoneTypeId === PhoneTypes.Softphone) {
            let phoneToAdd: PhoneRegisterListItem = {
               ...this.formService.getValue(this.phone.phoneId, this.phone.extensionId),
            };
            phoneToAdd = this.setUniquePhoneIds(phoneToAdd);
            phoneToAdd.sipUsers?.forEach((sipUser) => {
               sipUser.accountId = this.accountId; // accountId is required in API
            });
            this.store.dispatch(addPhoneToRegister({ phone: phoneToAdd }));
            this.formService.markAsUntouchedAndPristine();
            this.onCloseClick();
         }
         if (this.phone.phoneTypeId === PhoneTypes.Hardphone) {
            let phoneToAdd: PhoneRegisterListItem = {
               ...this.formService.getHardphoneValue(this.phone.phoneId, this.phone.extensionId),
            };
            phoneToAdd = this.setUniquePhoneIds(phoneToAdd);
            this.store.dispatch(addPhoneToRegister({ phone: phoneToAdd }));
            this.formService.markAsUntouchedAndPristine();
            this.onCloseClick();
         }
         if (this.phone.phoneTypeId === PhoneTypes.Handset) {
            let phoneToAdd: PhoneRegisterListItem = {
               ...this.formService.getHandsetValue(this.phone.phoneId, this.phone.extensionId),
            };
            phoneToAdd = this.setUniquePhoneIds(phoneToAdd);
            this.store.dispatch(addPhoneToRegister({ phone: phoneToAdd }));
            this.formService.markAsUntouchedAndPristine();
            this.onCloseClick();
         }
      } else {
         this.dialogService.close();
         this.formService.markAsUntouchedAndPristine();
      }
   }

   setUniquePhoneIds(phoneToAdd: PhoneRegisterListItem): PhoneRegisterListItem {
      if (phoneToAdd.phoneId === 0) {
         // new phones have phoneId = 0 but register expects a unique phoneId
         this.store.pipe(select(selectPhones), take(1)).subscribe((phones) => {
            if (phoneToAdd.phoneType === PhoneTypes.Hardphone) {
               const matchingPhone = phones.find((phone) => {
                  return phone.name === phoneToAdd.name;
               });
               if (matchingPhone) {
                  phoneToAdd.phoneId = matchingPhone.phoneId;
                  return phoneToAdd;
               }
            }
            if (phones.length > 0) {
               phoneToAdd.phoneId =
                  Math.max(
                     ...phones.map((phone) => {
                        return phone.phoneId ?? 0;
                     })
                  ) + 1;
            } else {
               phoneToAdd.phoneId = 1;
            }
         });
      }
      return phoneToAdd;
   }
}
